import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type KatonMeetMode = 'full' | 'pip';

export interface KatonMeetSession {
  sessionId: string;
  meetingId?: string | null;
  title?: string;
}

interface KatonMeetContextType {
  session: KatonMeetSession | null;
  mode: KatonMeetMode;
  openMeeting: (session: KatonMeetSession) => boolean;
  minimize: () => void;
  expand: () => void;
  closeMeeting: () => void;
}

const KatonMeetContext = createContext<KatonMeetContextType | null>(null);

export const KatonMeetProvider: FC<{ children: ReactNode; isSignedIn?: boolean }> = ({ children, isSignedIn = true }) => {
  const [session, setSession] = useState<KatonMeetSession | null>(null);
  const [mode, setMode] = useState<KatonMeetMode>('full');
  const sessionRef = useRef<KatonMeetSession | null>(null);

  // Returns false when a different class meeting is already open
  const openMeeting = useCallback((next: KatonMeetSession) => {
    const current = sessionRef.current;
    if (current && current.sessionId !== next.sessionId) {
      return false;
    }
    if (!current) {
      sessionRef.current = next;
      setSession(next);
    }
    setMode('full');
    return true;
  }, []);

  const minimize = useCallback(() => setMode('pip'), []);
  const expand = useCallback(() => setMode('full'), []);

  const closeMeeting = useCallback(() => {
    sessionRef.current = null;
    setSession(null);
    setMode('full');
  }, []);

  useEffect(() => {
    if (!isSignedIn) closeMeeting();
  }, [isSignedIn, closeMeeting]);

  const value = useMemo(
    () => ({ session, mode, openMeeting, minimize, expand, closeMeeting }),
    [session, mode, openMeeting, minimize, expand, closeMeeting],
  );

  return <KatonMeetContext.Provider value={value}>{children}</KatonMeetContext.Provider>;
};

// Same context, or null outside KatonMeetProvider, for code that also renders where no meeting can be open
export const useOptionalKatonMeet = () => useContext(KatonMeetContext);

export const useKatonMeet = () => {
  const context = useContext(KatonMeetContext);
  if (!context) {
    throw new Error('useKatonMeet must be used within KatonMeetProvider');
  }
  return context;
};
